import { getRequiredBrowseData as browseGetRequiredBrowseData, getCurrentRowElement, performDistinctMultiSelectEELSearch, setCurrentRecord } from "#skyward_browse";
import { addBrowse, getBrowseById, getInnerBrowse, prependOpenBrowse, removeBrowse } from "#skyward_browseList";
import { reportException, SkywardException } from "#skyward_error";
import { calculateTextWidthPx, deepEquals, mobileAccess, virtualDir } from "#skyward_global";
import { Binding, ModalLayer, showCodeEELDialog, showErrorDialog } from "#skyward_modalLayer";
import { hasVerticalScrollBar } from "#skyward_window";
import { encodeHtml, getIdGenerator, isNullOrEmpty, SequentialOperationHandler } from "./skyward.base.extensions";
import { DependencySnapshot, getDependencyDataWithoutWaiting, getRequestDataHash, HashTransaction, waitForDependencies } from "./skyward.base.requestDataHash";
import { getValue as controlsGetValue, setValue as controlsSetValue, getScopeContainer } from "./skyward.ui.controls";
import debounce from "./skyward.ui.debounce";
import { registerEvents } from "./skyward.ui.events";
import { callLoadContentFromUrl, getTopMostWorkflow, WorkflowData } from "./skyward.ui.workflow";
import { Browse, JQueryInput } from "./types";

interface DistinctMultiSelectEELInitialData
{
    additionalfieldinfo?: string;
    sortfieldinfo?: string;
    filter: string;
    readonlyselecteditemids?: string[];
    forceSingleThread?: boolean;
    hiddenBackingDataId: string | null;
    isSingleSelect?: boolean;
    dataModule: string;
    dataObject: string;
    routeModule: string;
    routeObject: string;
    routeHash: string;
    requestDataHash: string;
    distinctfield: string;
    rendernodatarow: boolean;
    hidedistinctfield: boolean;
    distinctfieldwidth: string;
    browseName: string;
    browseId: string;
    isOnTheFly: boolean;
    rowsPerPage: number;
    dynamicfilterfield: string;
    dynamicfiltervalue: string;
    queryParameterData?: Record<string, unknown> | null;
    hidePrimaryBrowseButton: boolean;
    allowMassSelect: boolean;
    tempTableName: string;
    browseType?: string;
    refreshTimeout?: number;
    distinctMultiSelectEELSearchText?: string;
    promptDataCollection?: string;
    requestDataHashSrc: string;
    requestDataHashReadonly?: boolean;
    dmseInstance?: DMSEInstance | undefined;
}

interface DMSEInstance
{
    remove: (targetId: string | number) => void;
    unselectAll: (suppressChange?: boolean) => void;
    add: (targetId: string, targetHash: string, displayText: string, suppressChangeEvent?: boolean) => void;
    doSearch: (searchText: string, seekId?: number | undefined) => any;
    showPopup: () => void;
    // Triggers a change event, which normally will redraw UI.
    setBackingData: (value: Record<string, string>, hashes: Record<string, string> | null, dontRebuildTiles: boolean, suppressChangeEvent?: boolean) => Promise<void>;

    selectedItems: JQuery<HTMLElement>;
    distinctMultiSelectEELData: DistinctMultiSelectEELData;
}

export interface DistinctMultiSelectEELData extends Omit<DistinctMultiSelectEELInitialData, | 'hiddenBackingDataId' | 'readonlyselecteditemids'>
{
    selectedItemKeys: string[];
    selectedItemHashes: string[];
    selectedKeyValuePairs: Record<string, string>;
    selectedKeyValueHashes: Record<string, string>;
    hiddenBackingData: JQuery<HTMLInputElement>;
    dynamicFilterHashes: Record<string, string | null>;
    readOnlySelectedItemIDs?: string[] | undefined;
    textbox: JQuery<HTMLInputElement>;
    layer: ModalLayer | null;
    button?: JQuery;
}

interface DistinctMultiSelectEELPublicData
{
    browseType: "DistinctMultiSelect";
    refreshTimeout: number;
    selectedItemKeys: string;
    filter: string;
    sortfieldinfo: string;
    distinctMultiSelectEELSearchText: string;
    rowsPerPage?: number;
    promptDataCollection?: string;
}

interface PromptDataItem
{
    UniqueID: string;
    BoundValue: any;
    Type: string;
    PresenceType: string;
    DataType: string;
    IsHidden: boolean;
}

const classNames =
{
    distinctMultiSelectEEL: "distinctMultiSelectEEL",
    distinctMultiSelectSearchButton: "distinctMultiSelectSearchButton",
    distinctMultiSelectUserInput: "distinctMultiSelectUserInput",
    distinctMultiSelectClearButton: "distinctMultiSelectClearButton",
    distinctMultiSelectContainer: "distinctMultiSelectContainer",
    dmsBackingData: "dmsBackingData",
    dummyTile: "dummyTile",
    selectedItems: "selectedItems",
    selectedItem: "selectedItem",
    selectedItemRemoveIcon: "selectedItem__removeIcon",
    eventsBound: "eventsBound--DMSE",
    unlockedBrowseBody: "unlockedBrowseBody",
    selectDistinctMultiSelectEELBrowseRow: "selectDistinctMultiSelectEELBrowseRow",
    browse: "browse",
    checkboxMassSelectButton: "checkbox-mass-select-button",
    jsMassSelectCell: "js-massSelectCell",
    current: "current",
    browseRow: "browseRow",
    checkboxButton: "checkbox-button",
    browseDataCell: "browseDataCell",
    checkbox: "checkbox",
    uiDisabled: "ui-disabled",
    browseBody: "browseBody",
    noneChecked: "noneChecked",
    returnValueListAsKeys: "returnValueListAsKeys",
    modalLayerContainer: "modalLayerContainer",
    hasDependentPrompts: "hasDependentPrompts",
    workflowContent: "workflowContent",
    currentRecordContent: "currentRecordContent",
    carbonCopyOptionFilterWrapper: "carbonCopyOptionFilterWrapper",
    filterValue: "filterValue",
    multipleSelectBox: "multipleSelectBox",
    multiSelectBackingData: "multiSelectBackingData",
    promptFieldSet: "promptFieldSet"
};

const skyward = window.skyward;

const dmseDataByBrowse = new WeakMap<HTMLElement, DistinctMultiSelectEELData>();
const dmseIdGenerator = getIdGenerator("popup", "_distinctMultiSelectEELBrowse");

const events =
{
    init
};

registerEvents("distinctMultiSelectEEL", events);

function init(selector: JQueryInput)
{
    const dmse = $(selector);
    registerDistinctMultiSelectEEL(dmse);
}

export function registerDistinctMultiSelectEEL(distinctMultiSelectEEL: JQuery<HTMLElement>): DMSEInstance
{
    if (distinctMultiSelectEEL.hasClass("dmsBackingData"))
    {
        distinctMultiSelectEEL = distinctMultiSelectEEL.closest(".distinctMultiSelectEEL");
    }
    if (distinctMultiSelectEEL.hasClass(classNames.eventsBound) && (distinctMultiSelectEEL.data("dmseInstance") as DMSEInstance)?.distinctMultiSelectEELData.hiddenBackingData.parent()[0] === distinctMultiSelectEEL[0])
    {
        return distinctMultiSelectEEL.data("dmseInstance");
    }

    let lastValue: string | null = null;
    let layer: ModalLayer | { onscreen: false } = { onscreen: false };
    const data = distinctMultiSelectEEL.data() as DistinctMultiSelectEELInitialData;
    // Override automatic JSON-Parsing of additionalFields to preserve type on the return trip.
    data.additionalfieldinfo = distinctMultiSelectEEL.attr("data-additionalfieldinfo");
    data.sortfieldinfo = distinctMultiSelectEEL.attr("data-sortfieldinfo");
    let browseId: string | undefined;

    const selectedItems = distinctMultiSelectEEL.find(`ul.${classNames.selectedItems}`);
    selectedItems.find("a").prop("tabIndex", -1);
    const magnifyingGlass = selectedItems.children(`.${classNames.distinctMultiSelectSearchButton}`);
    const textbox = selectedItems.find(`.${classNames.distinctMultiSelectUserInput}`) as JQuery<HTMLInputElement>;
    const placeholder = textbox && textbox.length > 0 ? textbox[0].placeholder : null;
    const clearButton = distinctMultiSelectEEL.children(`.${classNames.distinctMultiSelectClearButton}`);
    const container = distinctMultiSelectEEL.find(`.${classNames.distinctMultiSelectContainer}`);

    showHidePlaceholder();
    showHideClearButton();

    const label = distinctMultiSelectEEL.findLabel();
    if (label.length)
    {
        label.on('click', function ()
        {
            if (!textbox.is(document.activeElement!))
            {
                textbox.trigger("focus");
            }
        });
        const labelId = label.addIds().attr("id")!;
        distinctMultiSelectEEL.attr("aria-labelledby", labelId);
        const textboxID = textbox.attr("id");
        if (label[0].tagName === "LABEL" && textboxID)
        {
            label.attr("for", textboxID);
        }
        else
        {
            textbox.attr("aria-labelledby", labelId);
        }  
    }

    selectedItems.find(".selectedItem").off('click').on('click', removeTileButtonClicked);
    selectedItems.on('click', function ()
    {
        if (!textbox.is(document.activeElement!))
        {
            textbox.trigger("focus");
        }
    });

    const dummyTile = selectedItems.find(`li.${classNames.dummyTile}`);

    const distinctMultiSelectEELData: DistinctMultiSelectEELData =
    {
        ...data,
        selectedItemKeys: [],
        selectedItemHashes: [],
        selectedKeyValuePairs: {},
        selectedKeyValueHashes: {},
        filter: JSON.stringify(data.filter),
        hiddenBackingData: distinctMultiSelectEEL.find(".dmsBackingData") as JQuery<HTMLInputElement>,
        readOnlySelectedItemIDs: data.readonlyselecteditemids,
        textbox: textbox,
        layer: null,
        forceSingleThread: data.forceSingleThread || false,
        dynamicFilterHashes: {},
    };

    const singleRequestHandler = new SequentialOperationHandler(true);
    const transaction = new HashTransaction(distinctMultiSelectEELData.hiddenBackingData);

    if (!data.requestDataHashReadonly)
    {
        loadRequestDataHashes(data.requestDataHashSrc, distinctMultiSelectEEL, distinctMultiSelectEELData);
    }

    // Register selected items
    selectedItems.find(`li.${classNames.selectedItem}`).not(`.${classNames.dummyTile}`).each(function ()
    {
        const item = $(this);
        const targetId = item.data("targetid") + "";
        const targetHash = item.data("targethash");
        if (distinctMultiSelectEELData.selectedItemKeys.indexOf(targetId) === -1)
        {
            distinctMultiSelectEELData.selectedItemKeys.push(targetId);
            distinctMultiSelectEELData.selectedItemHashes.push(targetHash);
            distinctMultiSelectEELData.selectedKeyValuePairs[targetId] = item.text().trim().replace(/\r\n|\r|\n|\u0085|\u2028|\u2029/g, " ");
            distinctMultiSelectEELData.selectedKeyValueHashes[targetId] = targetHash;
        }
    });

    distinctMultiSelectEEL.find(`.${classNames.distinctMultiSelectSearchButton}`).off("click").on("click", function ()
    {
        if ($(this).is(`.${classNames.uiDisabled}`))
        {
            return false;
        }

        if (layer.onscreen)
        {
            layer.close();
        }
        else
        {
            showPopup();
            doSearch(textbox.val()!, undefined);
        }

        return;
    });

    distinctMultiSelectEEL.children(`.${classNames.distinctMultiSelectClearButton}`).off("click").on("click", function ()
    {
        if (layer.onscreen && !mobileAccess)
        {
            layer.close();
        }

        unselectAll();
    });

    resizeTextInput();

    distinctMultiSelectEELData.hiddenBackingData.on("workflowEnd", function (_eventData: JQuery.Event, workflowData: WorkflowData & { updateControl?: { pageStateControl: string, primaryKey: number }, redirectURL?: string })
    {
        if ($.isEmptyObject(workflowData.updateControl) || distinctMultiSelectEELData.hiddenBackingData.attr("id") !== workflowData.updateControl!.pageStateControl)
        {
            return false;
        }

        showPopup();
        doSearch("", workflowData.updateControl!.primaryKey).then(function ()
        {
            const browse = getInnerBrowse(distinctMultiSelectEELData.layer!.container);
            if (browse === undefined || browse === null)
            {
                return;
            }
            const browseId = browse.id;
            // wait for the browse to load, so we can select the item that was added
            $(document).one("browseLoaded." + browseId, function ()
            {
                const browse = getBrowseById(browseId)!;
                const hasAnotherWorkflow = workflowData.redirectURL !== "";
                let itemWasFound = false;

                if (browse && browse.browse)
                {
                    const checkbox = browse.browse.find(`.${classNames.checkboxButton}${getTargetIDSelector(workflowData.updateControl?.primaryKey!)}`).find(`.${classNames.checkbox}`);
                    itemWasFound = checkbox.length > 0;
                    if (itemWasFound && !checkbox.is(":checked"))
                    {
                        // simulate a click on the checkbox for the item.  This will update add the selected item to the DMSE.
                        checkbox.trigger("click");
                    }
                }

                // if there is a workflow, we want to close the DMSE so it doesn't appear in front of the workflow
                if (getTopMostWorkflow().length)
                {
                    distinctMultiSelectEELData.layer!.close();
                }

                let deferred: PromiseLike<any> = $.Deferred().resolve().promise();

                // if the item wasn't found, display a message to the user
                if (!itemWasFound)
                {
                    const errorDescriptionText = `There is a Filter applied to this List that is preventing the item from being selected. Try adding a new item or go to the ${browse.routeObject} List to modify your item.`;
                    deferred = showErrorDialog("The item you added cannot be made available for this list.", errorDescriptionText);
                }

                // In the event of a "Save & Add Another" scenario, we'll need to launch the next workflow
                if (hasAnotherWorkflow)
                {
                    deferred.then(function ()
                    {
                        callLoadContentFromUrl(workflowData);
                    });
                }
            });
        });

        return true;
    });

    distinctMultiSelectEELData.hiddenBackingData.on("change", rebuildTilesFromBackingData);
    distinctMultiSelectEELData.hiddenBackingData.on("forceRebuild", rebuildTilesFromBackingData);

    textbox.on("keydown", function (event)
    {
        const eventKeys = skyward.keys.getKeyNamesFromEvent(event);
        const eventKeyIsPrintableChar = skyward.keys.isPrintableCharacter(event.keyCode);
        const isTriggerForDoSearch = eventKeys.ENTER || eventKeys.ARROW_DOWN || eventKeys.ARROW_UP || eventKeys.SPACE;
        const eventKeyIsDesirable = isTriggerForDoSearch || eventKeyIsPrintableChar;

        // If key isn't desirable or Tab then return true, the character is not one we want
        // to potentially use in the handler, i.e. ignore it and return focus
        if (!eventKeyIsDesirable || eventKeys.TAB)
        {
            return;
        }

        let continuePropagation = true;
        const textboxValue = textbox.val()!;
        const textboxIsEmpty = textboxValue.length === 0;

        if (layer.onscreen)
        {
            if (eventKeys.ENTER)
            {
                continuePropagation = false;
            }
        }
        else
        {
            showPopup();

            if (isTriggerForDoSearch)
            {
                doSearch(textboxValue, undefined);
                event.preventDefault();
                continuePropagation = false;
            }
        }
        if (eventKeys.BACKSPACE && textboxIsEmpty)
        {
            textbox.parent().prevAll().not(`.${classNames.dummyTile}`).first().find(`.${classNames.selectedItemRemoveIcon}`).trigger("click");
            searchTextChanged();
            textbox.trigger("focus");
        }

        return continuePropagation;
    });

    distinctMultiSelectEEL.on("clearAll", (_event, suppressChange: boolean) => unselectAll(suppressChange));
    const clearData = distinctMultiSelectEEL.data("clear");
    if (clearData !== undefined)
    {
        unselectAll(clearData);
        distinctMultiSelectEEL.removeData("clear");
    }

    distinctMultiSelectEEL.addClass(classNames.eventsBound);

    const instance =
    {
        remove,
        unselectAll,
        add,
        doSearch,
        showPopup,
        selectedItems,
        setBackingData,

        distinctMultiSelectEELData,
    };
    data.dmseInstance = instance;

    distinctMultiSelectEELData.hiddenBackingData.trigger("controlLoaded");
    return instance;

    function removeTileButtonClicked(this: HTMLElement)
    {
        const element = $(this);
        const isDisabled = element.closest(".ui-disabled").length > 0;
        const hasRemoveButton = element.has(`.${classNames.selectedItemRemoveIcon}`).length > 0;
        if (isDisabled || !hasRemoveButton)
        {
            return;
        }

        const targetId = element.closest("li").data("targetid");
        remove(targetId);
        updateBrowseCheckbox(targetId, false);

        setBackingData(distinctMultiSelectEELData.selectedKeyValuePairs, distinctMultiSelectEELData.selectedKeyValueHashes, true);

        refreshRelatedUI();

        return false;
    }

    function remove(targetId: string | number)
    {
        const selectedIndex = distinctMultiSelectEELData.selectedItemKeys.findIndex(id =>
        {
            const parsedId = typeof targetId === "number" ? parseInt(id) : id;
            return targetId === parsedId;
        });
        if (selectedIndex !== -1)
        {
            distinctMultiSelectEELData.selectedItemKeys.splice(selectedIndex, 1);
            distinctMultiSelectEELData.selectedItemHashes.splice(selectedIndex, 1);
        }
        delete distinctMultiSelectEELData.selectedKeyValuePairs[targetId];
        delete distinctMultiSelectEELData.selectedKeyValueHashes[targetId];

        const tile = selectedItems.find(getTargetIDSelector(targetId));
        tile.remove();

        scrollInputIntoView();
    }

    function updateBrowseCheckbox(targetId: string | number, value: boolean)
    {
        if (layer.onscreen && layer.container instanceof $)
        {
            // uncheck related checkbox
            const checkbox = layer.container
                .find(`.${classNames.unlockedBrowseBody} .${classNames.selectDistinctMultiSelectEELBrowseRow}`)
                .filter(getTargetIDSelector(targetId))
                .children("input");
            // ignore the next jschange event, (triggered when we change the property)
            checkbox.prop("checked", value);
        }
    }

    function unselectAll(suppressChange: boolean = false)
    {
        const valuesToUse: Record<string, string> = {};
        const hashesToUse: Record<string, string> = {};
        if ((distinctMultiSelectEELData.readOnlySelectedItemIDs || []).length > 0)
        {
            const backingKeyValuePairs = Object.entries(JSON.parse(distinctMultiSelectEELData.hiddenBackingData.val()!) as Record<string, string>);
            const backingHashes = (distinctMultiSelectEELData.hiddenBackingData.data("selectedValueHash") as string ?? "").split(",").filter(Boolean);
            for (let i = 0; i < backingKeyValuePairs.length; i += 1)
            {
                const [key, value] = backingKeyValuePairs[i];
                const hash = backingHashes[i];
                if (distinctMultiSelectEELData.readOnlySelectedItemIDs!.some(selectedItemID => key == selectedItemID))
                {
                    valuesToUse[key] = value;
                    hashesToUse[key] = hash;
                }
            }
        }

        distinctMultiSelectEELData.selectedKeyValuePairs = valuesToUse;
        distinctMultiSelectEELData.selectedKeyValueHashes = hashesToUse;
        setBackingData(valuesToUse, hashesToUse, false, suppressChange);

        if (layer.onscreen && layer.container instanceof $)
        {
            layer.container.find(`.${classNames.unlockedBrowseBody} .${classNames.selectDistinctMultiSelectEELBrowseRow}`).children("input").prop("checked", false);
        }
    }

    function add(targetId: string, targetHash: string, displayText: string, suppressChangeEvent: boolean = false)
    {
        if (distinctMultiSelectEELData.isSingleSelect)
        {
            distinctMultiSelectEELData.selectedItemKeys.push(targetId);
            distinctMultiSelectEELData.selectedItemHashes.push(targetHash);
        }
        else if (distinctMultiSelectEELData.selectedItemKeys.indexOf(targetId) === -1)
        {
            distinctMultiSelectEELData.selectedItemKeys.push(targetId);
            distinctMultiSelectEELData.selectedItemHashes.push(targetHash);
        }
        distinctMultiSelectEELData.selectedKeyValuePairs[targetId] = displayText;
        distinctMultiSelectEELData.selectedKeyValueHashes[targetId] = targetHash;

        //DMSE UI - formerly function addTile(targetId, displayText)
        if (!selectedItems.has(getTargetIDSelector(targetId)).length)
        {
            const newTile = dummyTile.clone();
            // Apply an attr so we can use a [attr] search later to locate this tile
            newTile.attr("data-targetid", targetId);
            newTile.attr("data-targethash", targetHash);
            newTile.find("span").text((displayText || "").replace(/\r\n|\r|\n|\u0085|\u2028|\u2029/g, " "));
            newTile.removeClass(classNames.dummyTile);
            newTile.insertBefore(dummyTile);
            newTile.off('click').on('click', removeTileButtonClicked);

            if (!suppressChangeEvent)
            {
                scrollInputIntoView();
            }
        }
    }

    function scrollInputIntoView()
    {
        textbox.trigger("select");
        textbox.scrollintoview({ duration: 0 });

        distinctMultiSelectEEL.scrollintoview({ duration: 0 });
    }

    function doSearch(searchText: string, seekId?: number): Promise<void>
    {
        if ($.isEmptyObject(distinctMultiSelectEELData.layer) || !distinctMultiSelectEELData.layer!.container)
        {
            return Promise.reject();
        }
        const browse = distinctMultiSelectEELData.layer!.container.find(`.${classNames.browse}`);
        browseId = browse.attr("id")!;

        // Attach key list somewhere we can access it from GetData();
        dmseDataByBrowse.set(browse[0], distinctMultiSelectEELData);
        const browseData = $.extend({}, distinctMultiSelectEELData);

        if (seekId !== undefined)
        {
            $.extend(browseData, { seekId: seekId });
        }
        else
        {
            $.extend(browseData, { distinctMultiSelectEELSearchText: encodeHtml(searchText) });
        }
        return singleRequestHandler.run((signal) =>
        {
            return performDistinctMultiSelectEELSearch(browseId!, browseData as any, signal).promise();
        });
    }

    function setMassSelectButton()
    {
        if ($.isEmptyObject(layer) || !((layer as ModalLayer).container instanceof $))
        {
            return;
        }
        distinctMultiSelectEELData.button = (layer as ModalLayer).container.find(`.${classNames.checkboxMassSelectButton}`) as JQuery<HTMLButtonElement>;
        distinctMultiSelectEELData.button.on("click", function ()
        {
            setBrowseCurrentRecord(-1);
        });
    }

    function setBrowseCurrentRecord(row: number)
    {
        setCurrentRecord(browseId!, row, { loadRelatedBrowses: false });
    }

    function getBrowsePostData()
    {
        return container.data("postdata");
    }

    function getRequiredBrowseData(browseId: string, popupBrowse: JQuery | undefined, dependencies: DependencySnapshot): Browse
    {
        const registeredBrowseData: Browse =
        {
            id: browseId,
            browse: popupBrowse,
            browseType: "DistinctMultiSelect",
            routeModule: distinctMultiSelectEELData.routeModule,
            routeObject: distinctMultiSelectEELData.routeObject,
            pageModule: distinctMultiSelectEELData.dataModule,
            pageObject: distinctMultiSelectEELData.dataObject,
            pageScreen: "",
            dataModule: distinctMultiSelectEELData.dataModule,
            dataObject: distinctMultiSelectEELData.dataObject,
            routeHash: distinctMultiSelectEELData.routeHash,
            requestDataHash: distinctMultiSelectEELData.requestDataHash,
            refreshTimeout: -1,
            filter: distinctMultiSelectEELData.filter,
            keyEventsEnabled: true,
            additionalfieldinfo: distinctMultiSelectEELData.additionalfieldinfo,
            sortfieldinfo: distinctMultiSelectEELData.sortfieldinfo,
            distinctfield: distinctMultiSelectEELData.distinctfield,
            rendernodatarow: distinctMultiSelectEELData.rendernodatarow,
            hidedistinctfield: distinctMultiSelectEELData.hidedistinctfield,
            distinctfieldwidth: distinctMultiSelectEELData.distinctfieldwidth,
            selectedItemKeys: distinctMultiSelectEELData.selectedItemKeys,
            browseName: distinctMultiSelectEELData.browseName,
            browseId: distinctMultiSelectEELData.browseId,
            isOnTheFly: distinctMultiSelectEELData.isOnTheFly,
            successCallback: setMassSelectButton,
            data: getBrowsePostData(),
            dynamicFilterHashes: distinctMultiSelectEELData.dynamicFilterHashes,
            dynamicFilterValues: dependencies,
            queryParameterData: distinctMultiSelectEELData.queryParameterData,
            hidePrimaryBrowseButton: distinctMultiSelectEELData.hidePrimaryBrowseButton,
            hiddenBackingDataId: distinctMultiSelectEELData.hiddenBackingData.attr('id'),
            rowsPerPage: distinctMultiSelectEELData.rowsPerPage,

            taskName: "",
            primaryKeys: [],
            resize: function () { },
        };
        return registeredBrowseData;
    }

    function showPopup()
    {
        removeBrowse(browseId!);
        browseId = dmseIdGenerator.getNewId();
        textbox.attr("aria-expanded", "true");


        const popupContent = $("<div>", { "class": "popup--browse", id: browseId + "_popup" });
        const popupBrowse = $("<div>", { id: browseId, "class": `${classNames.browse} browseStaticHeight` });
        popupContent.append(popupBrowse);
        // TODO (6756543) wait for dependencies before opening the popup
        const dependencies = getDependencyDataWithoutWaiting(distinctMultiSelectEELData, getScopeContainer(distinctMultiSelectEEL));
        const registeredBrowseData = getRequiredBrowseData(browseId, popupBrowse, dependencies);

        addBrowse(registeredBrowseData, browseId);

        prependOpenBrowse(browseId);

        const beforeClose = function ()
        {
            // When the modal layer closes, browses within it will be hidden, so abort open requests.
            singleRequestHandler.close();
            textbox.val("");
            if (!mobileAccess)
            {
                textbox.trigger("focus");
            }

            lastValue = null;

            if (registeredBrowseData.rowsPerPage)
            {
                distinctMultiSelectEELData.rowsPerPage = registeredBrowseData.rowsPerPage;
            }

            // null out our references to the layer so it can be garbage-collected
            layer = { onscreen: false };
            distinctMultiSelectEELData.layer = null;
            container.off("wheel", onlyScrollSelectedItems);
        };

        const afterClose = function ()
        {
            distinctMultiSelectEEL.trigger("modalClosed");
            textbox.attr("aria-expanded", "false");
        };

        const bindings: Binding[] =
            [
                {
                    onEvents: "input",
                    bindTo: textbox,
                    runFunction: resizeTextInput
                } as Binding,
                {
                    onEvents: "input",
                    bindTo: textbox,
                    runFunction: debounce(searchTextChanged)
                } as Binding,
                {
                    onEvents: "keydown",
                    bindTo: textbox,
                    runFunction: function (event: JQuery.KeyDownEvent)
                    {
                        const eventKeys = skyward.keys.getKeyNamesFromEvent(event);

                        if (eventKeys.TAB)
                        {
                            (layer as ModalLayer).close();
                        }
                        else if (eventKeys.ENTER)
                        {
                            const selectAllCellSelected = (layer as ModalLayer).contents.find(`.${classNames.jsMassSelectCell}.${classNames.current}`);
                            if (selectAllCellSelected.length > 0)
                            {
                                if (distinctMultiSelectEELData.button)
                                {
                                    distinctMultiSelectEELData.button.trigger("click");
                                }
                            }
                            else
                            {
                                (layer as ModalLayer).contents.find(`.${classNames.browseRow}.${classNames.current} .${classNames.selectDistinctMultiSelectEELBrowseRow}`).first().trigger("click");
                            }
                            event.preventDefault();
                        }
                    }
                } as Binding,
                {
                    onEvents: "click",
                    bindTo: magnifyingGlass,
                    runFunction: closeLayer
                } as Binding,
                {
                    onEvents: `rowChanged browseLoaded.${browseId}`,
                    bindTo: popupBrowse,
                    runFunction: function (_event: JQuery.Event)
                    {
                        const browse = getBrowseById(browseId!)!;
                        const row = getCurrentRowElement("unlocked", browseId!, browse.currentRow!, "DistinctMultiSelect");
                        textbox.attr("aria-activedescendant", row.addIds().attr("id")!);
                    }
                } as Binding,
            ];

        layer = showCodeEELDialog(popupContent, textbox, container, beforeClose, distinctMultiSelectEELData, bindings, afterClose);

        const selectedItemsElement = selectedItems.get(0)!;
        container.on("wheel", onlyScrollSelectedItems);

        layer.container.off("change").on("change", `.${classNames.checkbox}`, null, checkboxClicked);
        layer.container.data("scopedMassSelect", scopedMassSelect);

        textbox.trigger("focus");

        function closeLayer()
        {
            (layer as ModalLayer).close();
        }

        function onlyScrollSelectedItems(scrollEvent: JQuery.TriggeredEvent)
        {
            if (!hasVerticalScrollBar(selectedItems))
            {
                scrollEvent.preventDefault();
                return;
            }

            const scrolledToTop = selectedItemsElement.scrollTop === 0;
            const originalEvent = scrollEvent.originalEvent as WheelEvent;
            const scrollingUp = originalEvent.deltaY < 0;
            if (scrolledToTop && scrollingUp)
            {
                scrollEvent.preventDefault();
                return;
            }

            const scrolledToBottom = selectedItemsElement.scrollTop + selectedItems.outerHeight()! === selectedItemsElement.scrollHeight;
            const scrollingDown = originalEvent.deltaY > 0;
            if (scrolledToBottom && scrollingDown)
            {
                scrollEvent.preventDefault();
                return;
            }

            const targetElement = scrollEvent.target;
            const targetOustideOfSelectedItems = selectedItemsElement !== targetElement && !$.contains(selectedItemsElement, targetElement);
            if (targetOustideOfSelectedItems)
            {
                scrollEvent.preventDefault();
                return;
            }
        }
    }

    function resizeTextInput()
    {
        const inputPadding = 20;

        const textWidth = calculateTextWidthPx(textbox.val()!) + inputPadding;
        const placeholderWidth = calculateTextWidthPx(textbox.attr("placeholder") ?? "") + inputPadding; // use current placeholder value, not the original value

        textbox.css(
            {
                "width": Math.max(textWidth, placeholderWidth) + "px"
            });
    }

    function checkboxClicked(this: HTMLInputElement): void
    {
        const checkbox = $(this);
        const checkboxButton = $(this).closest(`.${classNames.checkboxButton}`);
        let targetId = checkboxButton.data("targetid");
        targetId = targetId !== null ? targetId.toString() : "";
        const rowIndex = checkbox.closest("[data-row]").data("row");
        const targetHash = getBrowseById(browseId!)!.rowDataHash![rowIndex];
        const row = checkboxButton.closest("tr");
        const displayText = row.find(`.${classNames.browseDataCell}`).first().text();

        const checked = checkbox.is(":checked");

        if (checked)
        {
            if (distinctMultiSelectEELData.isSingleSelect)
            {
                unselectAll(true);
            }
            add(targetId, targetHash, displayText, false);
        }
        else
        {
            remove(targetId);
        }

        if (distinctMultiSelectEELData.isSingleSelect)
        {
            (layer as ModalLayer).close();
        }

        setBrowseCurrentRecord(row.prevAll().length);

        setBackingData(distinctMultiSelectEELData.selectedKeyValuePairs, distinctMultiSelectEELData.selectedKeyValueHashes, true);

        toggleCheckedAllOfId(row.closest(`.${classNames.browseBody}`), targetId, checked);
        refreshRelatedUI();
    }

    function toggleCheckedAllOfId(browseBody: JQuery<HTMLElement>, targetId: string, checked: boolean)
    {
        browseBody.find(`.${classNames.checkboxButton}`).each(function (this: HTMLElement)
        {
            const button = $(this);
            const buttonId = button.data('targetid');
            if (buttonId === targetId)
            {
                controlsSetValue(button.children(`.${classNames.checkbox}`), checked, true);
            }
        });
    }

    function searchTextChanged()
    {
        // Since this function can be called from a debounce, we want to ensure that the user still has focus within the input
        // when this function is called. For instance, if a user presses tab we don't want to return focus to the search input.
        if (document.activeElement != textbox.get(0))
        {
            return;
        }
        const searchText: string = textbox.val()!;
        resizeTextInput();

        if (lastValue !== null && searchText.toLowerCase() === lastValue.toLowerCase())
        {
            return;
        }

        if (distinctMultiSelectEELData.layer == null)
        {
            showPopup();
        }

        lastValue = searchText;
        doSearch(searchText, undefined);
    }

    function scopedMassSelect(buttonElement: HTMLElement)
    {
        const button = $(buttonElement);
        const browse = getBrowseById(browseId!);
        if (!browse)
        {
            return null;
        }

        if (button.hasClass(classNames.noneChecked))
        {
            const browseData = browseGetRequiredBrowseData(browse, {});
            const url = virtualDir + browse.routeModule + "/" + browse.routeObject + '/SelectAll';

            const searchText = { distinctMultiSelectEELSearchText: encodeHtml(textbox.val()!) };
            $.extend(browseData, searchText);

            $.ajax(
                {
                    url: url,
                    type: "POST",
                    dataType: "JSON",
                    activityIndicator: { hideGlass: true },
                    data: browseData,
                    success: function (data: { id: string; hash: string; name: string }[])
                    {
                        for (const { id, hash, name } of data)
                        {
                            add(id, hash, name, false);
                            updateBrowseCheckbox(id, true);
                        }

                        updateBackingDataFromDOM();
                        refreshRelatedUI();
                    }
                }
            );
        }
        else
        {
            unselectAll();
        }

        return $.Deferred().reject();
    }

    function getTargetIDSelector(targetID: string | number)
    {
        return "[data-targetid='" + CSS.escape(targetID.toString()) + "']";
    }

    function showHideClearButton()
    {
        if (selectedItems.has("[data-targetid]").length > 0)
        {
            clearButton.show();
        }
        else
        {
            clearButton.hide();
        }
    }

    function showHidePlaceholder()
    {
        showHidePlaceholderShared(selectedItems, textbox, placeholder);
        resizeTextInput();
    }

    /**
     * @param suppressChangeEvent Normally, a change event will be triggered which redraws the UI. This flag prevents this event.
     */
    async function setBackingData(values: Record<string, string> | null, hashes: Record<string, string | null> | null, dontRebuildTiles: boolean, suppressChangeEvent = false)
    {
        const dataValues = values ?? {};

        if (dontRebuildTiles)
        {
            distinctMultiSelectEELData.hiddenBackingData.off("change", rebuildTilesFromBackingData);
        }

        const sortedEntries = Object.fromEntries(
            Object.entries(dataValues)
                .sort((entryA, entryB) => dataValues[entryA[0]].localeCompare(dataValues[entryB[0]]))
        );

        const valueKeys = Object.keys(sortedEntries);
        let hashArray = hashes ? valueKeys.map(key => hashes[key]) : [];

        if (valueKeys.length > 0 && (hashArray.length !== valueKeys.length || hashArray.some(isNullOrEmpty)))
        {
            const valueHashes = await singleRequestHandler.run(signal => transaction.run(signal, async () => validateValues(signal, valueKeys)));
            if (valueHashes === null)
            {
                reportException(new Error("validateValues returned null due to a cancelled request"));
                return;
            }

            hashArray = valueKeys.map(key => valueHashes[key]);

            if (hashArray.some(isNullOrEmpty))
            {
                reportException(new SkywardException("Distinct multi select values are missing hashes.", JSON.stringify(
                {
                    controlName: distinctMultiSelectEELData.hiddenBackingData.data("original-name") as string | undefined ?? distinctMultiSelectEELData.hiddenBackingData.attr("name"),
                    dataModule: distinctMultiSelectEELData.dataModule,
                    dataObject: distinctMultiSelectEELData.dataObject,
                    distinctField: distinctMultiSelectEELData.distinctfield,
                    hashesProvided: hashes !== null,
                    valueKeys: valueKeys,
                    returnedKeys: Object.keys(valueHashes),
                    keysWithoutHash: valueKeys.filter((_key, index) => isNullOrEmpty(hashArray[index]))
                })));
            }
        }

        const data = distinctMultiSelectEELData.hiddenBackingData;
        const stringValue = JSON.stringify(sortedEntries);

        data.val(stringValue);
        data.data("selectedValueHash", hashArray.join(","));

        if (!suppressChangeEvent)
        {
            data.trigger("change", { suppressChangeEvent, suppressRebuildHashes: true });
        }
        else
        {
            data.trigger("forceRebuild", { suppressChangeEvent, suppressRebuildHashes: true });
        }

        if (dontRebuildTiles)
        {
            distinctMultiSelectEELData.hiddenBackingData.on("change", rebuildTilesFromBackingData);
        }
    }

    interface ValidateDistinctMultiSelectEELResponse
    {
        id: string;
        hash: string;
    }

    async function validateValues(signal: AbortSignal, values: string[])
    {
        const dependencies = await waitForDependencies(distinctMultiSelectEELData, getScopeContainer(distinctMultiSelectEEL), signal);
        const browse = getRequiredBrowseData("temp", $(), dependencies);
        const postData = browseGetRequiredBrowseData(browse, { tempTableName: distinctMultiSelectEELData.tempTableName });
        postData.selectedValues = JSON.stringify(values);

        const response = await $.ajax(
        {
            url: `${virtualDir}${distinctMultiSelectEELData.routeModule}/${distinctMultiSelectEELData.routeObject}/ValidateDistinctMultiSelectEEL`,
            type: "POST",
            dataType: "JSON",
            data: postData,
            activityIndicator: { hideGlass: true },
            signal: signal
        }) as ValidateDistinctMultiSelectEELResponse[] | null;

        if (!response)
        {
            return null;
        }

        const valueHashes: Record<string, string> = {};

        for (const item of response)
        {
            valueHashes[item.id] = item.hash;
        }

        return valueHashes;
    }

    async function updateBackingDataFromDOM()
    {
        distinctMultiSelectEELData.selectedKeyValuePairs = {};
        distinctMultiSelectEELData.selectedKeyValueHashes = {};
        distinctMultiSelectEELData.selectedItemKeys = [];
        distinctMultiSelectEELData.selectedItemHashes = [];
        scrapeTokensForSelectedValues(distinctMultiSelectEELData.hiddenBackingData, function (key, value, hash)
        {
            distinctMultiSelectEELData.selectedItemKeys.push(key);
            distinctMultiSelectEELData.selectedKeyValuePairs[key] = value;
            distinctMultiSelectEELData.selectedKeyValueHashes[key] = hash;
        });

        await setBackingData(distinctMultiSelectEELData.selectedKeyValuePairs, distinctMultiSelectEELData.selectedKeyValueHashes, true);
    }

    async function rebuildTilesFromBackingData(_event: JQuery.TriggeredEvent, options: { suppressChangeEvent?: boolean, suppressRebuildHashes?: boolean })
    {
        const { suppressChangeEvent = false, suppressRebuildHashes = false } = options ?? {};

        const oldHash = distinctMultiSelectEELData.hiddenBackingData.data().selectedValueHash;
        return singleRequestHandler.run((signal) => transaction.run(signal, async () =>
        {
            distinctMultiSelectEELData.selectedItemKeys = [];
            distinctMultiSelectEELData.selectedItemHashes = [];
            distinctMultiSelectEELData.selectedKeyValuePairs = {};
            distinctMultiSelectEELData.selectedKeyValueHashes = {};

            $(`#${browseId}`).data('selectedItemKeys', distinctMultiSelectEELData.selectedItemKeys);

            const tiles = selectedItems.find(`li:not(.${classNames.dummyTile})`).has(`.${classNames.selectedItemRemoveIcon}`);
            tiles.remove();

            let backingHashes: string[] = [];
            const backingKeyPairs = Object.entries(JSON.parse(distinctMultiSelectEELData.hiddenBackingData.val()!) as Record<string, string>);
            if (!$.isEmptyObject(backingKeyPairs) && !suppressRebuildHashes)
            {
                if (deepEquals(backingKeyPairs, Object.entries(distinctMultiSelectEELData.hiddenBackingData.data("originalValue"))))
                {
                    backingHashes = distinctMultiSelectEELData.hiddenBackingData.data("originalSelectedValueHash").split(",");
                }
                else
                {
                    const valueKeys = backingKeyPairs.map(kvp => kvp[0]);
                    const validatedValues = await validateValues(signal, valueKeys);
                    if (validatedValues === null)
                    {
                        return;
                    }
                    backingHashes = valueKeys.map(key => validatedValues[key]);
                }
            }
            else
            {
                backingHashes = (oldHash as string ?? "").split(",").filter(Boolean);
            }

            for (let i = 0; i < backingKeyPairs.length; i += 1)
            {
                const [key, value] = backingKeyPairs[i];
                const hash = backingHashes[i];
                add(key, hash, value, suppressChangeEvent);
                updateBrowseCheckbox(key, true);
            }

            distinctMultiSelectEELData.hiddenBackingData.data("selectedValueHash", backingHashes.join(","));
            refreshRelatedUI();
        }));
    }

    function refreshRelatedUI()
    {
        if (layer.onscreen && typeof layer.options?.resizeCallback === "function")
        {
            layer.options.resizeCallback();
        }

        if (distinctMultiSelectEELData.button)
        {
            distinctMultiSelectEELData.button
                .data("selectedRecordCount", distinctMultiSelectEELData.selectedItemKeys.length)
                .trigger("selectedRecordCountChanged");
        }

        if (!distinctMultiSelectEEL.hasClass("ui-disabled"))
        {
            showHideClearButton();
            showHidePlaceholder();
        }
    }
}

export function changeTracking(element: JQuery, valueArg: Record<string, string> | string[], originalValueArg: Record<string, string> | string[])
{
    let value: string[];
    let originalValue: string[];

    if (!element.hasClass(classNames.returnValueListAsKeys))
    {
        value = Object.keys(valueArg);
        originalValue = Object.keys(originalValueArg);
    }
    else
    {
        value = valueArg as string[];
        originalValue = originalValueArg as string[];
    }

    return value.length === originalValue.length && $(value).not(originalValue as any).length === 0 && $(originalValue).not(value as any).length === 0;
}

export function isEmpty(backingDataElement: JQuery<HTMLElement>)
{
    const stringValue = backingDataElement.val();
    return !stringValue || stringValue === "{}";
}

export function massSelect(button: JQueryInput)
{
    const scopedMassSelect = $(button).closest(`div.${classNames.modalLayerContainer}`).data("scopedMassSelect");
    if (typeof scopedMassSelect === 'function')
    {
        return scopedMassSelect(button);
    }
}

export function getData(browse: Browse): DistinctMultiSelectEELPublicData
{
    const browseElem = browse.browse;
    const dmseData = dmseDataByBrowse.get(browseElem?.[0]!);
    const data: DistinctMultiSelectEELPublicData =
    {
        browseType: "DistinctMultiSelect",
        refreshTimeout: -1,
        selectedItemKeys: encodeHtml(JSON.stringify(dmseData?.selectedItemKeys ?? [])),
        filter: encodeHtml(browse.filter!),
        sortfieldinfo: browse.sortfieldinfo as string,
        distinctMultiSelectEELSearchText: encodeHtml(dmseData?.textbox.val() ?? ""),
    };

    if (browse.rowsPerPage)
    {
        data.rowsPerPage = browse.rowsPerPage;
    }

    if (browse.hiddenBackingDataId && (browse.hiddenBackingDataId as string).length > 0)
    {
        const promptDataCollection = buildPromptDataCollection(browse.hiddenBackingDataId as string);
        data.promptDataCollection = JSON.stringify(promptDataCollection);
    }
    return data;
}

export function set(element: JQuery<HTMLElement>, value: string | object, suppressChangeEvent: boolean = false)
{
    const dmse = registerDistinctMultiSelectEEL(element);
    const objValue = typeof value === "string" ? JSON.parse(value) : value as Record<string, string>;
    return dmse.setBackingData(objValue, null, false, suppressChangeEvent);
}

function scrapeTokensForSelectedValues(backingDataElement: JQuery<HTMLInputElement>, callback: (key: string, value: string, hash: string) => void)
{
    backingDataElement.siblings(`.${classNames.distinctMultiSelectContainer}`).find(`.${classNames.selectedItems}`).find(`.${classNames.selectedItem}`).not(`.${classNames.dummyTile}`).each(function (this: HTMLElement)
    {
        const selectedItem = $(this);
        let key = selectedItem.data().targetid;
        key = typeof key === "number" ? key.toString() : key;
        const value = selectedItem.text().trim();
        const hash = selectedItem.data().targethash;
        callback(key, value, hash);
    });
}

function showHidePlaceholderShared(selectedItems: JQuery<HTMLElement>, textbox: JQuery<HTMLInputElement>, placeholder: string | null)
{
    if (!placeholder)
    {
        return;
    }

    if (selectedItems.has("[data-targetid]").length)
    {
        textbox[0].placeholder = "";
    }
    else
    {
        textbox[0].placeholder = placeholder;
    }
}

function buildPromptDataCollection(hiddenBackingDataID: string)
{
    const hiddenBackingDataElement = $("#" + hiddenBackingDataID);
    const promptDataCollection: PromptDataItem[] = [];
    if (hiddenBackingDataElement.hasClass(classNames.hasDependentPrompts))
    {
        const dependentPromptUniqueIds = hiddenBackingDataElement.data().dependentPromptUniqueIds;

        let closestContentSelector = `.${classNames.workflowContent}, .${classNames.currentRecordContent}`;
        if (hiddenBackingDataElement.parents().hasClass(classNames.carbonCopyOptionFilterWrapper))
        {
            //carbon copy option filters should only get dependent values inside it's own area
            closestContentSelector = `.${classNames.carbonCopyOptionFilterWrapper}`
        }
        const content = hiddenBackingDataElement.closest(closestContentSelector);

        for (let i = 0; i < dependentPromptUniqueIds.length; i++)
        {
            const dependentPromptUniqueId = dependentPromptUniqueIds[i];
            const dependentPrompt = content.find(`.${classNames.filterValue}[data-prompt-unique-id='${dependentPromptUniqueId}']`)
            const isDistinctMultiSelect = dependentPrompt.hasClass(classNames.distinctMultiSelectEEL);
            const isMultiSelectBox = dependentPrompt.hasClass(classNames.multipleSelectBox);
            let dependentPromptValue = null;

            if (isDistinctMultiSelect)
            {
                dependentPromptValue = controlsGetValue(dependentPrompt.find(`.${classNames.dmsBackingData}`));
            }
            else if (isMultiSelectBox)
            {
                dependentPromptValue = controlsGetValue(dependentPrompt.find(`.${classNames.multiSelectBackingData}`));
            }
            else
            {
                dependentPromptValue = controlsGetValue(dependentPrompt);
            }

            let promptData = dependentPrompt.data();
            let isHidden = dependentPrompt.closest(`.${classNames.promptFieldSet}`).is(":hidden");
            promptDataCollection.push({
                UniqueID: dependentPromptUniqueId,
                BoundValue: isHidden ? null : dependentPromptValue,
                Type: promptData.promptType,
                PresenceType: promptData.presenceType,
                DataType: promptData.fieldType,
                IsHidden: isHidden,
            });
        }
    }
    return promptDataCollection;
}

function loadRequestDataHashes(requestDataHashSrc: string, container: JQuery, dmseData: DistinctMultiSelectEELData)
{
    const id = dmseData.hiddenBackingData.data("original-name") ?? dmseData.hiddenBackingData.attr("name")!;
    const requestData = getRequestDataHash(requestDataHashSrc, id);
    if (!requestData)
    {
        return;
    }
    dmseData.requestDataHash = requestData.hash;
    const hashes = requestData.initialValues!;
    const dmseHiddenData = dmseData.hiddenBackingData.data();
    dmseData.dynamicFilterHashes = { ...requestData.dependencies };
    const buttonData: Record<string, any> = {};
    for (const button of container.find("[data-targetid]"))
    {
        const data = $(button).data();
        buttonData[data.targetid] = data;
    }
    const targetids = Object.keys(JSON.parse(dmseData.hiddenBackingData.val()!) as Record<string, string>);
    const orderedHashes: string[] = [];
    for (let i = 0; i < targetids.length; i += 1)
    {
        const hash = hashes[targetids[i]];
        buttonData[targetids[i]].targethash = hash;
        orderedHashes.push(hash);
    }
    dmseHiddenData.selectedValueHash = dmseHiddenData.originalSelectedValueHash = orderedHashes.join(",");
}
